import {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { isRTL, type SupportedLocale } from '@/i18n/config';

/* ─── Context shape ─── */
interface ThemeContextValue {
  /** Current document direction based on active locale. */
  dir: 'ltr' | 'rtl';
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/* ─── Provider ─── */
interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { i18n } = useTranslation();

  const currentLang = (i18n.language ?? 'en') as SupportedLocale;
  const dir: 'ltr' | 'rtl' = isRTL(currentLang) ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.dir = dir;
    document.documentElement.lang = currentLang;
  }, [dir, currentLang]);

  return (
    <ThemeContext.Provider value={{ dir }}>
      {children}
    </ThemeContext.Provider>
  );
}

/* ─── Hook ─── */
/**
 * Returns the current theme context (direction).
 * Must be used within a ThemeProvider.
 */
export function useThemeContext(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return ctx;
}
